<template>
    <AppLayout>
        <template #icon>
            <div class="h-10 w-10 flex items-center justify-center">
                <BookingsHeaderLogo />
            </div>
        </template>
        <template #title> {{ $t("bookings.title") }} </template>
        <template #description> {{ $t("bookings.description") }} </template>
        <div class="pb-20">
            <div>
                <div>
                    <div class="p-4 sm:p-6">
                        <div
                            class="gap-3 sm:flex-row sm:items-center sm:justify-between flex flex-col"
                        >
                            <div
                                class="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto"
                            >
                                <div>
                                    <nav
                                        class="gap-x-1 sm:gap-x-2 flex"
                                        aria-label="Tabs"
                                    >
                                        <inertia-link
                                            v-for="tab in tabs"
                                            :key="tab.name"
                                            :href="tab.href"
                                            :class="[
                                                'px-3 py-2 text-sm font-medium inline-flex shrink-0 items-center text-center whitespace-nowrap',
                                                tab.current
                                                    ? 'rounded-lg bg-iron-200 text-iron-700 text-center'
                                                    : 'text-iron-500 hover:border-iron-300 hover:text-iron-700',
                                            ]"
                                        >
                                            {{ tab.name }}
                                            <span
                                                class="ml-2 rounded text-xs px-[5px] leading-[18px]"
                                                :class="[
                                                    tab.current
                                                        ? 'bg-iron-700 text-white'
                                                        : 'bg-iron-200 text-iron-700',
                                                ]"
                                                >{{ tab.count }}</span
                                            >
                                        </inertia-link>
                                    </nav>
                                </div>
                            </div>

                            <div class="gap-2 flex">
                                <template v-if="teams.length > 0">
                                    <Select
                                        :model-value="selectedTeamId"
                                        @update:model-value="showByTeam"
                                    >
                                        <SelectTrigger>
                                            <SelectValue
                                                :placeholder="
                                                    $t('bookings.by_team')
                                                "
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectGroup>
                                                <SelectItem
                                                    class="px-4"
                                                    v-for="team of teams"
                                                    :key="team.id"
                                                    :value="team.id.toString()"
                                                >
                                                    {{ team.name }}
                                                </SelectItem>
                                            </SelectGroup>
                                        </SelectContent>
                                    </Select>

                                    <Button
                                        @click="cancelTeamFilter"
                                        v-if="selectedTeamId"
                                        variant="outline"
                                        ><X :size="16"
                                    /></Button>
                                </template>

                                <DropdownMenu>
                                    <DropdownMenuTrigger as-child>
                                        <Button variant="outline">
                                            <Download :size="16" class="mr-2" />
                                            {{ $t("bookings.export") }}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent :align="'end'">
                                        <DropdownMenuItem
                                            @click="exportReservations('csv')"
                                        >
                                            {{ $t("bookings.export_csv") }}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            @click="exportReservations('pdf')"
                                        >
                                            {{ $t("bookings.export_pdf") }}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>

                        <div
                            v-if="reservationDateGroups.length !== 0"
                            class="pt-6"
                        >
                            <div v-auto-animate>
                                <div
                                    v-for="(
                                        reservations, date
                                    ) in reservationDateGroups"
                                    :key="date"
                                    class="mb-4"
                                >
                                    <Event
                                        v-for="reservation in reservations"
                                        :key="reservation.id"
                                        class="my-4"
                                        :reservation="reservation"
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            v-else
                            class="py-12 flex w-full items-center justify-center"
                        >
                            <EmptyStateWithIcon
                                :title="$t('bookings.empty_state.title')"
                                :description="
                                    $t('bookings.empty_state.description')
                                "
                            >
                                <template #icon>
                                    <svg
                                        width="76"
                                        height="76"
                                        viewBox="0 0 76 76"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M22.0219 14.1338L24.2397 24.5674M42.8891 9.6984L45.1068 20.132M62.5842 38.227L58.1488 17.3598C57.8547 15.9762 57.023 14.7662 55.8367 13.9958C54.6505 13.2254 53.2067 12.9578 51.8231 13.2519L15.3056 21.0139C13.9221 21.308 12.712 22.1397 11.9416 23.326C11.1712 24.5123 10.9036 25.956 11.1977 27.3396L18.9597 63.8571C19.2538 65.2407 20.0855 66.4507 21.2718 67.2211C22.4581 67.9915 23.9018 68.2591 25.2854 67.965L46.1525 63.5296M13.4154 37.7732L60.3665 27.7934M52.8689 56.6495L59.1945 60.7574L67.4104 48.1061"
                                            stroke="url(#paint0_linear_620_2321)"
                                            stroke-width="4"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_620_2321"
                                                x1="15.4711"
                                                y1="20.98"
                                                x2="64.0716"
                                                y2="53.3919"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#FF9B47" />
                                                <stop
                                                    offset="0.147634"
                                                    stop-color="#FF7447"
                                                />
                                                <stop
                                                    offset="0.454086"
                                                    stop-color="#FF4247"
                                                />
                                                <stop
                                                    offset="0.636926"
                                                    stop-color="#E92A5B"
                                                />
                                                <stop
                                                    offset="0.740367"
                                                    stop-color="#DD1C67"
                                                />
                                                <stop
                                                    offset="0.842991"
                                                    stop-color="#D31071"
                                                />
                                                <stop
                                                    offset="1"
                                                    stop-color="#CF0283"
                                                />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </template>
                            </EmptyStateWithIcon>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script setup lang="ts">
import AppLayout from "../../../../Layouts/AppLayout.vue";
import { ref, computed } from "vue";
import route from "ziggy-js";
import Event from "./components/event.vue";
import { useI18n } from "vue-i18n";
import { EmptyStateWithIcon } from "@/components/empty-state-with-icon";
import { router, usePage } from "@inertiajs/vue3";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Download, X } from "lucide-vue-next";
import { vAutoAnimate } from "@formkit/auto-animate/vue";
import BookingsHeaderLogo from "./components/BookingsHeaderLogo.svg";

const page = usePage();

const props = defineProps({
    reservationDateGroups: {
        type: Object,
        required: true,
    },
    upcomingReservationsCount: {
        type: Number,
        required: true,
    },
    cancelledReservationsCount: {
        type: Number,
        required: true,
    },
    pastReservationsCount: {
        type: Number,
        required: true,
    },
});

const teams = computed(() => {
    //@ts-expect-error
    return page.props.allTeams.filter(
        (team: any) => team.personal_team === false,
    );
});

const { t } = useI18n();

const selectedTeamId = ref();

function showByTeam(id: string) {
    selectedTeamId.value = id;
    router.visit(route("app.dashboard", { teamId: id }), {
        preserveState: true,
    });
}

function cancelTeamFilter() {
    selectedTeamId.value = undefined;
    router.visit(route("app.dashboard", { teamId: undefined }), {
        preserveState: true,
    });
}

const currentType = computed(() => {
    if (route().current("app.dashboard.past")) {
        return "past";
    }
    if (route().current("app.dashboard.cancelled")) {
        return "cancelled";
    }
    return "upcoming";
});

function exportReservations(format: "csv" | "pdf") {
    const routeName =
        format === "csv"
            ? "app.dashboard.export.csv"
            : "app.dashboard.export.pdf";

    const params: Record<string, string> = { type: currentType.value };

    if (selectedTeamId.value) {
        params.teamId = selectedTeamId.value;
    }

    window.location.href = route(routeName, params);
}

const tabs = ref([
    {
        name: t("bookings.upcoming"),
        href: route("app.dashboard.upcoming"),
        current:
            route().current("app.dashboard") ||
            route().current("app.dashboard.upcoming"),
        count: props.upcomingReservationsCount,
    },
    // {
    //     name: t("bookings.pending"),
    //     href: "#",
    //     current: false,
    // },
    {
        name: t("bookings.past"),
        href: route("app.dashboard.past"),
        current: route().current("app.dashboard.past"),
        count: props.pastReservationsCount,
    },
    {
        name: t("bookings.cancelled"),
        href: route("app.dashboard.cancelled"),
        current: route().current("app.dashboard.cancelled"),
        count: props.cancelledReservationsCount,
    },
]);
</script>
